/**
 * Режим правки окна карточки площадки.
 *
 * Окно открывается на чтение; «Править» (кнопка, клавиша E) делает редактируемыми
 * текст, характеристики и фото. «Сохранить и отправить…» открывает подтверждение,
 * затем `window-edit` с `send` и `publish_confirmed`. Ответ 202 `{jobId}`
 * отслеживается через общий механизм фоновых задач; по завершении окно
 * перечитывается, а итог (отправлено, отклонено, выключена отправка) остаётся
 * на экране до закрытия диалога.
 *
 * Бренд и габариты правятся там, где состояние окна говорит `editable`
 * (ВБ — бренд и габариты, ОЗОН — бренд, Маркет — ничего): у закрытого
 * поля правка в запрос не попадает. Штрихкод не правится нигде: ОЗОН выдаёт его
 * сама, поэтому окно показывает его только на чтение. Бэкенд при закрытом поле отвечает
 * `FIELD_NOT_EDITABLE` и не сохраняет ничего, поэтому окно такие поля не шлёт.
 *
 * Чего окно не умеет и говорит об этом честно: добавить новую характеристику и
 * очистить значение нельзя (пустое поле не отправляется), цена не правится,
 * заблокированные характеристики (`locked`) — только чтение.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuthStore } from '../../../../stores/authStore'
import { useBackgroundJobsStore } from '../../../../stores/backgroundJobsStore'
import { characteristicText } from './model'
import {
  cardWindowApi,
  parseWindowEditOutcome,
  windowEditErrorInfo,
  type CardWindowState,
  type WindowDimensionsChange,
  type WindowEditChanges,
  type WindowEditErrorInfo,
  type WindowEditJobOutcome,
} from './windowEditApi'

export const MAX_PHOTOS = 30
export const MAX_BRAND_LENGTH = 255

export type DimensionAxis = 'length' | 'width' | 'height' | 'weight'

export const DIMENSION_AXES: DimensionAxis[] = ['length', 'width', 'height', 'weight']

export const DIMENSION_AXIS_LABELS: Record<DimensionAxis, string> = {
  length: 'Длина',
  width: 'Ширина',
  height: 'Высота',
  weight: 'Вес',
}

export type DimensionsDraft = Record<DimensionAxis, string>

export type EditPhase = 'idle' | 'confirm' | 'saving' | 'running' | 'done' | 'failed'

export interface McardEditState {
  editing: boolean
  title: string
  description: string
  /** Правки характеристик: target_id -> новое значение (только тронутые поля). */
  characteristics: Record<string, string>
  media: string[]
  brand: string
  /** Габариты строками из полей ввода: длины в см, вес в кг. */
  dimensions: DimensionsDraft
}

const EMPTY_DIMENSIONS: DimensionsDraft = { length: '', width: '', height: '', weight: '' }

const dimensionText = (axis: DimensionAxis, value: unknown): string => {
  if (value === null || value === undefined || value === '') return ''
  const number = Number(value)
  if (!Number.isFinite(number)) return ''
  return axis === 'weight' ? String(number).replace('.', ',') : String(number)
}

/** Число из поля ввода: запятая допустима; не число — NaN. */
const parseInputNumber = (text: string): number => Number(text.trim().replace(',', '.'))

export interface McardEditApi {
  editing: boolean
  draft: McardEditState
  setTitle: (value: string) => void
  setDescription: (value: string) => void
  setCharacteristic: (targetId: number, value: string) => void
  setMedia: (urls: string[]) => void
  setBrand: (value: string) => void
  setDimension: (axis: DimensionAxis, value: string) => void
  start: () => void
  cancel: () => void
  /** Что реально изменилось, готовое к отправке. */
  changes: WindowEditChanges
  /** Названия изменённых полей для футера и подтверждения. */
  dirtyLabels: string[]
  hasChanges: boolean
  /** Причины, по которым отправлять нельзя. */
  violations: string[]
  phase: EditPhase
  openConfirm: () => void
  closeConfirm: () => void
  confirm: () => Promise<void>
  /** Закрыть итоговый диалог и вернуться в режим чтения. */
  finish: () => void
  error: WindowEditErrorInfo | null
  outcome: WindowEditJobOutcome | null
  jobId: string | null
  /** Шаги и прогресс фоновой задачи для диалога. */
  job: ReturnType<typeof useJobView>
}

interface Options {
  sellerId: number | null
  cardId: number | null
  state: CardWindowState | null
  /** Название кабинета для имени задачи в панели фоновых задач. */
  jobName: string
  /** Вызывается после завершения отправки: окно перечитывает карточку и состояние. */
  onDone: () => void
}

const sameList = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((value, index) => value === b[index])

function useJobView(jobId: string | null) {
  return useBackgroundJobsStore((store) => (jobId ? (store.jobs.get(jobId) ?? null) : null))
}

export function useMpCardEdit({ sellerId, cardId, state, jobName, onDone }: Options): McardEditApi {
  const [editing, setEditing] = useState(false)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [characteristics, setCharacteristics] = useState<Record<string, string>>({})
  const [media, setMedia] = useState<string[]>([])
  const [brand, setBrand] = useState('')
  const [dimensions, setDimensions] = useState<DimensionsDraft>(EMPTY_DIMENSIONS)
  const [phase, setPhase] = useState<EditPhase>('idle')
  const [error, setError] = useState<WindowEditErrorInfo | null>(null)
  const [jobId, setJobId] = useState<string | null>(null)
  const [outcome, setOutcome] = useState<WindowEditJobOutcome | null>(null)
  const job = useJobView(jobId)
  const doneHandled = useRef(false)

  const baseline = useMemo(
    () => ({
      title: state?.fields.title.value ?? '',
      description: state?.fields.description.value ?? '',
      media: state?.fields.media_urls.value ?? [],
      characteristics: new Map(
        (state?.fields.characteristics ?? [])
          .filter((item) => item.target_id !== null)
          .map((item) => [String(item.target_id), item] as const),
      ),
      brand: state?.fields.brand?.value ?? '',
      brandEditable: state?.fields.brand?.editable === true,
      dimensions: {
        length: dimensionText('length', state?.fields.dimensions?.value?.length),
        width: dimensionText('width', state?.fields.dimensions?.value?.width),
        height: dimensionText('height', state?.fields.dimensions?.value?.height),
        weight: dimensionText('weight', state?.fields.dimensions?.value?.weight),
      } as DimensionsDraft,
      dimensionsEditable: state?.fields.dimensions?.editable === true,
    }),
    [state],
  )

  const resetRequisites = useCallback(() => {
    setBrand(baseline.brand)
    setDimensions(baseline.dimensions)
  }, [baseline])

  const reset = useCallback(() => {
    setTitle(baseline.title)
    setDescription(baseline.description)
    setCharacteristics({})
    setMedia(baseline.media)
    resetRequisites()
  }, [baseline, resetRequisites])

  const start = useCallback(() => {
    if (!state) return
    reset()
    setEditing(true)
  }, [reset, state])

  const cancel = useCallback(() => {
    setEditing(false)
    setCharacteristics({})
    setTitle(baseline.title)
    setDescription(baseline.description)
    setMedia(baseline.media)
    resetRequisites()
    setPhase('idle')
    setError(null)
  }, [baseline, resetRequisites])

  // Состояние перечитали (после отправки или смены карточки): черновик правок сбрасываем.
  useEffect(() => {
    if (!editing) {
      setTitle(baseline.title)
      setDescription(baseline.description)
      setMedia(baseline.media)
      setCharacteristics({})
      resetRequisites()
    }
  }, [baseline, editing, resetRequisites])

  /** Реквизиты: что изменено, что уйдёт в запрос и что мешает отправке. */
  const requisites = useMemo(() => {
    const result: {
      brand?: string
      dimensions?: WindowDimensionsChange
      touched: { brand: boolean; dimensions: boolean }
      violations: string[]
    } = { touched: { brand: false, dimensions: false }, violations: [] }
    if (!editing) return result

    if (baseline.brandEditable && brand.trim() !== baseline.brand.trim()) {
      result.touched.brand = true
      if (!brand.trim()) result.violations.push('Бренд не может быть пустым')
      else if (brand.trim().length > MAX_BRAND_LENGTH) result.violations.push(`Бренд: не больше ${MAX_BRAND_LENGTH} символов`)
      else result.brand = brand.trim()
    }

    if (baseline.dimensionsEditable) {
      const changed: WindowDimensionsChange = {}
      for (const axis of DIMENSION_AXES) {
        const text = dimensions[axis].trim()
        const before = baseline.dimensions[axis].trim()
        if (text === before) continue
        const numeric = parseInputNumber(text)
        const beforeNumeric = parseInputNumber(before)
        if (text !== '' && before !== '' && numeric === beforeNumeric) continue
        result.touched.dimensions = true
        const label = DIMENSION_AXIS_LABELS[axis].toLowerCase()
        if (text === '') {
          result.violations.push(`Габариты: поле «${DIMENSION_AXIS_LABELS[axis]}» нельзя оставить пустым`)
        } else if (!Number.isFinite(numeric) || numeric <= 0) {
          result.violations.push(`Габариты: ${label} должна быть положительным числом`)
        } else if (axis === 'weight') {
          const kg = Math.round(numeric * 1000) / 1000
          if (kg <= 0) result.violations.push('Габариты: вес не меньше 1 г (0,001 кг)')
          else changed.weight = kg
        } else if (!Number.isInteger(numeric)) {
          result.violations.push(`Габариты: ${label} — целое число сантиметров`)
        } else {
          changed[axis] = numeric
        }
      }
      if (Object.keys(changed).length > 0) result.dimensions = changed
    }

    return result
  }, [baseline, brand, dimensions, editing])

  const changes = useMemo<WindowEditChanges>(() => {
    const result: WindowEditChanges = {}
    if (!editing) return result
    if (title.trim() !== baseline.title.trim()) result.title = title.trim()
    if (description.trim() !== baseline.description.trim()) result.description = description.trim()
    const changedChars: Record<string, string> = {}
    for (const [key, value] of Object.entries(characteristics)) {
      const current = characteristicText(baseline.characteristics.get(key)?.value)
      // Пустое значение не отправляется: очистить характеристику из окна нельзя.
      if (value.trim() && value.trim() !== current.trim()) changedChars[key] = value.trim()
    }
    if (Object.keys(changedChars).length > 0) result.characteristics = changedChars
    if (!sameList(media, baseline.media)) result.media_urls = media
    if (requisites.brand !== undefined) result.brand = requisites.brand
    if (requisites.dimensions !== undefined) result.dimensions = requisites.dimensions
    return result
  }, [baseline, characteristics, description, editing, media, requisites, title])

  const dirtyLabels = useMemo(() => {
    const labels: string[] = []
    if (changes.title !== undefined) labels.push('Наименование')
    if (changes.description !== undefined) labels.push('Описание')
    for (const key of Object.keys(changes.characteristics ?? {})) {
      labels.push(baseline.characteristics.get(key)?.name ?? `Характеристика ${key}`)
    }
    if (changes.media_urls !== undefined) labels.push('Фото')
    // Подписи реквизитов — по тронутым полям: некорректный ввод тоже виден в подтверждении.
    if (requisites.touched.brand) labels.push('Бренд')
    if (requisites.touched.dimensions) labels.push('Габариты')
    return labels
  }, [baseline, changes, requisites.touched])

  const violations = useMemo(() => {
    const list: string[] = [...requisites.violations]
    if (editing && !title.trim()) list.push('Наименование не может быть пустым')
    if (editing && !description.trim()) list.push('Описание не может быть пустым')
    if (changes.media_urls !== undefined) {
      if (changes.media_urls.length === 0) list.push('Нельзя оставить карточку без фотографий')
      if (changes.media_urls.length > MAX_PHOTOS) list.push(`Фотографий не больше ${MAX_PHOTOS}`)
      if (changes.media_urls.some((url) => !/^https?:\/\//i.test(url))) {
        list.push('Адрес каждого фото должен начинаться с http:// или https://')
      }
    }
    return list
  }, [changes.media_urls, description, editing, requisites.violations, title])

  const hasChanges = dirtyLabels.length > 0

  const openConfirm = useCallback(() => {
    if (!hasChanges) return
    setError(null)
    setPhase('confirm')
  }, [hasChanges])

  const closeConfirm = useCallback(() => {
    setPhase((current) => (current === 'confirm' ? 'idle' : current))
  }, [])

  const confirm = useCallback(async () => {
    if (sellerId === null || cardId === null || !hasChanges || violations.length > 0) return
    setPhase('saving')
    setError(null)
    doneHandled.current = false
    try {
      const result = await cardWindowApi.edit(sellerId, cardId, {
        changes,
        send: true,
        publish_confirmed: true,
      })
      if (!result.jobId) {
        // Без отправки: правка сохранена в ПИМ и журнале, задачи нет.
        setOutcome({
          status: 'channel_disabled',
          message: 'Правка сохранена в ПИМ и журнале, на площадку не отправлялась',
          reason: null,
          card_id: result.card_id,
          source_change_id: result.sourceChangeId,
          change_id: null,
          send_enabled: false,
        })
        setPhase('done')
        onDone()
        return
      }
      const store = useBackgroundJobsStore.getState()
      if (!store.jobs.has(result.jobId)) {
        store.addJob({
          jobId: result.jobId,
          userId: Number(useAuthStore.getState().user?.user_id ?? 0),
          type: 'marketplace_card_window_edit',
          status: 'pending',
          name: `Правка карточки: ${jobName}`,
          currentStep: 'Ожидание запуска',
          progress: { current: 0, total: 0, percentage: 0 },
          steps: [],
          startedAt: new Date(),
          metadata: {},
        })
      }
      setJobId(result.jobId)
      setPhase('running')
    } catch (requestError: any) {
      setError(windowEditErrorInfo(requestError, 'Не удалось сохранить правку'))
      setPhase('failed')
    }
  }, [cardId, changes, hasChanges, jobName, onDone, sellerId, violations.length])

  // Итог фоновой задачи.
  useEffect(() => {
    if (!job || doneHandled.current) return
    if (job.status === 'completed') {
      doneHandled.current = true
      setOutcome(parseWindowEditOutcome(job.metadata))
      setPhase('done')
      onDone()
    } else if (job.status === 'failed' || job.status === 'cancelled' || job.status === 'stopped') {
      doneHandled.current = true
      setError({ code: null, message: job.error || 'Фоновая задача завершилась с ошибкой' })
      setPhase('failed')
      // Правка уже лежит в ПИМ и журнале: перечитываем, чтобы окно показало актуальное.
      onDone()
    }
  }, [job, onDone])

  const finish = useCallback(() => {
    // Отказ при сохранении (задача не стартовала): правки в полях остаются, чтобы их можно было поправить.
    if (phase === 'failed' && !jobId) {
      setPhase('idle')
      setError(null)
      return
    }
    setEditing(false)
    setCharacteristics({})
    setPhase('idle')
    setJobId(null)
    setOutcome(null)
    setError(null)
  }, [jobId, phase])

  const setDimension = useCallback((axis: DimensionAxis, value: string) => {
    setDimensions((current) => ({ ...current, [axis]: value }))
  }, [])

  const setCharacteristic = useCallback((targetId: number, value: string) => {
    setCharacteristics((current) => ({ ...current, [String(targetId)]: value }))
  }, [])

  return {
    editing,
    draft: { editing, title, description, characteristics, media, brand, dimensions },
    setTitle,
    setDescription,
    setCharacteristic,
    setMedia,
    setBrand,
    setDimension,
    start,
    cancel,
    changes,
    dirtyLabels,
    hasChanges,
    violations,
    phase,
    openConfirm,
    closeConfirm,
    confirm,
    finish,
    error,
    outcome,
    jobId,
    job,
  }
}
